'use client';

import { useState, FormEvent, ChangeEvent } from 'react';

type ServiceType = 'volume' | 'msp' | 'rpo' | 'outsourcing';

const Home = () => {
  const [serviceType, setServiceType] = useState<ServiceType>('volume');
  const [result, setResult] = useState<string>('Заполните форму, чтобы увидеть расчёт.');
  const [formData, setFormData] = useState({
    hires: '',
    annual: '',
    spend: '',
    pct: '8',
    recs: '2',
    fee: '180000',
    cnt: '',
    base: '',
  });

  const [contactForm, setContactForm] = useState({
    company: '',
    name: '',
    email: '',
    phone: '',
    message: '',
  });

  const [isAgreed, setIsAgreed] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(null);

  const fmt = (n: number) =>
    new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB' }).format(n);

  const handleCalcInputChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { id, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [id]: value,
    }));
  };

  const handleContactInputChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setContactForm({ ...contactForm, [name]: value });
  };

  const handleAgreementChange = (e: ChangeEvent<HTMLInputElement>) => {
    setIsAgreed(e.target.checked);
    setErrorMessage(null);
  };

  const calculate = (e: FormEvent) => {
    e.preventDefault();

    let calculatedResult = '';
    const hires = parseInt(formData.hires) || 0;
    const annual = parseInt(formData.annual) || 0;
    const spend = parseInt(formData.spend) || 0;
    const pct = parseFloat(formData.pct) || 0;
    const recs = parseInt(formData.recs) || 0;
    const fee = parseInt(formData.fee) || 0;
    const cnt = parseInt(formData.cnt) || 0;
    const base = parseInt(formData.base) || 0;

    switch (serviceType) {
      case 'volume':
        let volumePct = 20;
        if (hires >= 11 && hires <= 30) volumePct = 18;
        if (hires >= 31) volumePct = 16;
        const monthlyVolume = hires * annual * (volumePct / 100);
        calculatedResult = `Оценка комиссии за месяц: ${fmt(monthlyVolume)} (тариф ${volumePct}% при ${hires} наймах/мес, средний доход ${fmt(annual)}).`;
        break;
      case 'msp':
        const monthlyMsp = spend * (pct / 100);
        calculatedResult = `MSP-плата: ${fmt(monthlyMsp)} в месяц (от совокупных затрат ${fmt(spend)} по ставке ${pct}%).`;
        break;
      case 'rpo':
        const monthlyRpo = recs * fee;
        calculatedResult = `RPO-ретейнер: ${fmt(monthlyRpo)} в месяц (команда ${recs} рекрутера × ${fmt(fee)}).`;
        break;
      case 'outsourcing':
        const opsPct = 25;
        const subtotal = cnt * base;
        const opsCost = subtotal * (opsPct / 100);
        const total = subtotal + opsCost;
        calculatedResult = `Аутсорсинг: база ${fmt(subtotal)} + операционный пакет (${opsPct}%): ${fmt(opsCost)} = ${fmt(total)} в мес.`;
        break;
    }
    setResult(calculatedResult);
  };

  const handleContactSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrorMessage(null);

    if (!isAgreed) {
      setErrorMessage('Пожалуйста, дайте согласие на обработку персональных данных.');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    const apiUrl = `${process.env.NEXT_PUBLIC_STRAPI_API_URL}/api/corporate-requests`;

    const payload = {
      formName: 'Решения для корпоративных партнеров',
      ...contactForm,
    };

    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ data: payload }),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setContactForm({ company: '', name: '', email: '', phone: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };


  const renderFields = () => {
    switch (serviceType) {
      case 'volume':
        return (
          <>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label htmlFor="hires" className="block text-sm font-medium text-gray-200 mb-1">
                  Наймов в месяц, шт.
                </label>
                <input
                  type="number"
                  id="hires"
                  name="hires"
                  min="1"
                  className="w-full rounded-md px-3 py-2 text-gray-900"
                  placeholder="напр., 12"
                  value={formData.hires}
                  onChange={handleCalcInputChange}
                  required
                />
              </div>
              <div>
                <label htmlFor="annual" className="block text-sm font-medium text-gray-200 mb-1">
                  Средний годовой доход, ₽
                </label>
                <input
                  type="number"
                  id="annual"
                  name="annual"
                  min="300000"
                  className="w-full rounded-md px-3 py-2 text-gray-900"
                  placeholder="напр., 1 200 000"
                  value={formData.annual}
                  onChange={handleCalcInputChange}
                  required
                />
              </div>
            </div>
            <p className="text-xs text-gray-300">
              Тариф: 1–10 наймов — 20%; 11–30 — 18%; 31+ — 16%.
            </p>
          </>
        );
      case 'msp':
        return (
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label htmlFor="spend" className="block text-sm font-medium text-gray-200 mb-1">
                Месячные затраты на подрядчиков, ₽
              </label>
              <input
                type="number"
                id="spend"
                name="spend"
                min="500000"
                className="w-full rounded-md px-3 py-2 text-gray-900"
                placeholder="напр., 3 000 000"
                value={formData.spend}
                onChange={handleCalcInputChange}
                required
              />
            </div>
            <div>
              <label htmlFor="pct" className="block text-sm font-medium text-gray-200 mb-1">
                Процент MSP, %
              </label>
              <input
                type="number"
                id="pct"
                name="pct"
                min="4"
                max="15"
                className="w-full rounded-md px-3 py-2 text-gray-900"
                value={formData.pct}
                onChange={handleCalcInputChange}
                required
              />
            </div>
          </div>
        );
      case 'rpo':
        return (
          <>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label htmlFor="recs" className="block text-sm font-medium text-gray-200 mb-1">
                  Рекрутеров в команде, чел.
                </label>
                <input
                  type="number"
                  id="recs"
                  name="recs"
                  min="1"
                  className="w-full rounded-md px-3 py-2 text-gray-900"
                  value={formData.recs}
                  onChange={handleCalcInputChange}
                  required
                />
              </div>
              <div>
                <label htmlFor="fee" className="block text-sm font-medium text-gray-200 mb-1">
                  Ставка за рекрутера, ₽/мес
                </label>
                <input
                  type="number"
                  id="fee"
                  name="fee"
                  min="120000"
                  className="w-full rounded-md px-3 py-2 text-gray-900"
                  value={formData.fee}
                  onChange={handleCalcInputChange}
                  required
                />
              </div>
            </div>
            <p className="text-xs text-gray-300">
              Включает TL, отчётность и интеграции. Закрепляется SLA.
            </p>
          </>
        );
      case 'outsourcing':
        return (
          <>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label htmlFor="cnt" className="block text-sm font-medium text-gray-200 mb-1">
                  Сотрудников в функции, чел.
                </label>
                <input
                  type="number"
                  id="cnt"
                  name="cnt"
                  min="3"
                  className="w-full rounded-md px-3 py-2 text-gray-900"
                  placeholder="напр., 15"
                  value={formData.cnt}
                  onChange={handleCalcInputChange}
                  required
                />
              </div>
              <div>
                <label htmlFor="base" className="block text-sm font-medium text-gray-200 mb-1">
                  Базовая ставка, ₽/мес/сотр.
                </label>
                <input
                  type="number"
                  id="base"
                  name="base"
                  min="20000"
                  className="w-full rounded-md px-3 py-2 text-gray-900"
                  placeholder="напр., 45 000"
                  value={formData.base}
                  onChange={handleCalcInputChange}
                  required
                />
              </div>
            </div>
            <p className="text-xs text-gray-300">Операционный пакет — 25% от базы.</p>
          </>
        );
    }
  };

  return (
    <div className="bg-gradient-to-br from-blue-900 to-blue-800 text-gray-100 antialiased">
      <header
        className="px-6 pt-32 pb-12 md:pt-40 md:pb-20 md:h-[505px]"
        style={{
          background: `
            linear-gradient(rgba(0,0,0,.45), rgba(0,0,0,.25)),
            radial-gradient(1200px 600px at 70% -10%, rgba(255,255,255,.08), transparent)`,
        }}
      >
        <div className="max-w-7xl mx-auto">
          <nav className="mb-6 flex flex-wrap items-center gap-3 text-sm">
            <a href="#programs" className="chip px-3 py-1 rounded-full hover:bg-white/25">
              Программы
            </a>
            <a href="#benefits" className="chip px-3 py-1 rounded-full hover:bg-white/25">
              Преимущества
            </a>
            <a href="#case" className="chip px-3 py-1 rounded-full hover:bg-white/25">
              Кейс
            </a>
            <a href="#calc" className="chip px-3 py-1 rounded-full hover:bg-white/25">
              Калькулятор
            </a>
            <a href="#faq" className="chip px-3 py-1 rounded-full hover:bg-white/25">
              FAQ
            </a>
            <a href="#contact" className="chip px-3 py-1 rounded-full hover:bg-white/25">
              Контакты
            </a>
          </nav>

          <h1 className="text-4xl md:text-6xl font-extrabold mb-4">
            Решения для корпоративных партнёров
          </h1>
          <p className="max-w-3xl text-lg md:text-xl text-gray-200">
            Масштабируем подбор, берём функции на аутсорсинг и управляем подрядчиками как единым портфелем. SLA, отчётность и интеграции.
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-4">
            <a
              href="#contact"
              className="bg-yellow-400 hover:bg-yellow-500 text-gray-900 px-6 py-3 rounded-xl font-semibold"
            >
              Запросить корпоративное КП
            </a>
            <a
              href="#programs"
              className="px-6 py-3 rounded-xl font-semibold inline-block bg-white text-[#003B9B] border-2 border-[#003B9B] transition-all duration-200 ease-in-out hover:brightness-[105%] hover:-translate-y-[1px] text-base"
            >
              Посмотреть программы
            </a>
          </div>
        </div>
      </header>

      <main className="px-6 py-12 md:py-16 max-w-7xl mx-auto space-y-16">
        <section id="programs" className="scroll-mt-32">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Корпоративные программы</h2>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            <article className="card rounded-2xl p-6">
              <h3 className="text-xl font-semibold mb-2">Объёмный подбор (Volume Hiring)</h3>
              <p className="text-sm text-gray-200 mb-3">
                Поток кандидатов и закрытие десятков вакансий ежемесячно. Скидка на комиссию по объёму.
              </p>
              <ul className="text-sm text-gray-200 space-y-2">
                <li>• Отраслевые воронки и сорсинг</li>
                <li>• Еженедельные отчёты и прогноз</li>
                <li>• Гарантия замены</li>
              </ul>
            </article>
            <article className="card rounded-2xl p-6">
              <h3 className="text-xl font-semibold mb-2">RPO — выделенная команда</h3>
              <p className="text-sm text-gray-200 mb-3">
                Наши рекрутеры и сорсеры работают как часть вашей команды по фиксированной ставке.
              </p>
              <ul className="text-sm text-gray-200 space-y-2">
                <li>• Выделенный TL и SLA</li>
                <li>• Интеграция в ATS/HRIS</li>
                <li>• Гибкое масштабирование</li>
              </ul>
            </article>
            <article className="card rounded-2xl p-6">
              <h3 className="text-xl font-semibold mb-2">MSP / Вендор-менеджмент</h3>
              <p className="text-sm text-gray-200 mb-3">
                Управляем всеми кадровыми подрядчиками, унифицируем ставки и отчётность.
              </p>
              <ul className="text-sm text-gray-200 space-y-2">
                <li>• Единые SLA и тарифная сетка</li>
                <li>• Контроль качества и затрат</li>
                <li>• Оцифровка табелей и KPI</li>
              </ul>
            </article>
            <article className="card rounded-2xl p-6">
              <h3 className="text-xl font-semibold mb-2">Аутсорсинг функций</h3>
              <p className="text-sm text-gray-200 mb-3">
                Берём на себя целый процесс: бэк-офис, call-center, склад, документооборот.
              </p>
              <ul className="text-sm text-gray-200 space-y-2">
                <li>• Регламенты, обучение, резерв</li>
                <li>• Контроль качества и отчёты</li>
                <li>• Фикс + 25% операционный пакет</li>
              </ul>
            </article>
            <article className="card rounded-2xl p-6">
              <h3 className="text-xl font-semibold mb-2">Партнёрства с вузами</h3>
              <p className="text-sm text-gray-200 mb-3">
                Программы стажировок, фреш-градуэйт-пулы и кадровые резервы.
              </p>
              <ul className="text-sm text-gray-200 space-y-2">
                <li>• Академический конвейер</li>
                <li>• Отбор и наставничество</li>
                <li>• KPI по трудоустройству</li>
              </ul>
            </article>
            <article className="card rounded-2xl p-6">
              <h3 className="text-xl font-semibold mb-2">Соответствие и аудит</h3>
              <p className="text-sm text-gray-200 mb-3">
                Риск-аудит, кадровые проверки и подготовка к инспекциям.
              </p>
              <ul className="text-sm text-gray-200 space-y-2">
                <li>• Политики, процедуры, документация</li>
                <li>• Тренинги для менеджеров</li>
                <li>• Ежеквартальные ревью</li>
              </ul>
            </article>
          </div>
        </section>

        <section id="benefits" className="scroll-mt-32">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Почему корпоративные клиенты выбирают нас
          </h2>
          <div className="grid gap-6 md:grid-cols-4">
            <div className="card rounded-2xl p-6">
              <div className="text-2xl font-bold mb-2">SLA & KPI</div>
              <p className="text-sm text-gray-200">
                Срок закрытия, явка, качество найма и замены — фиксируем финансовую ответственность.
              </p>
            </div>
            <div className="card rounded-2xl p-6">
              <div className="text-2xl font-bold mb-2">Отчётность</div>
              <p className="text-sm text-gray-200">
                Дашборды по воронке, стоимости найма и эффективности источников.
              </p>
            </div>
            <div className="card rounded-2xl p-6">
              <div className="text-2xl font-bold mb-2">Интеграции</div>
              <p className="text-sm text-gray-200">
                Подключаемся к вашим ATS/HRIS/BI и соблюдаем политики безопасности.
              </p>
            </div>
            <div className="card rounded-2xl p-6">
              <div className="text-2xl font-bold mb-2">Масштабирование</div>
              <p className="text-sm text-gray-200">
                Быстро увеличиваем команду под сезонные пики и новые регионы.
              </p>
            </div>
          </div>
        </section>

        <section id="case" className="scroll-mt-32">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Кейс: федеральный ритейл</h2>
          <div className="card rounded-2xl p-6 grid gap-6 md:grid-cols-3">
            <div className="md:col-span-2">
              <p className="text-sm text-gray-200">
                Масштабирование в 12 регионов: 180+ наймов за квартал, явка 96%, замена в течение 24–48 часов.
                Снижение CPH на 19% за счёт каннибализации источников и RPO-модели.
              </p>
              <ul className="text-sm text-gray-200 mt-4 space-y-2">
                <li>• 1 400+ релевантных резюме в месяц</li>
                <li>• Конверсия оффер → выход — 85%</li>
                <li>• Дашборд в BI и еженедельные встречи</li>
              </ul>
            </div>
            <div className="bg-white/5 rounded-xl p-4">
              <div className="text-4xl font-extrabold">-19%</div>
              <div className="text-xs text-gray-300">снижение Cost-per-Hire</div>
              <hr className="my-3 border-white/10" />
              <div className="text-4xl font-extrabold">96%</div>
              <div className="text-xs text-gray-300">средняя явка персонала</div>
              <hr className="my-3 border-white/10" />
              <div className="text-4xl font-extrabold">24–48ч</div>
              <div className="text-xs text-gray-300">SLA на замену</div>
            </div>
          </div>
        </section>

        <section id="calc" className="scroll-mt-32">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Калькулятор корпоративного контракта</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="simple-card">
              <form onSubmit={calculate} className="space-y-4">
                <div>
                  <label htmlFor="serviceType" className="block text-sm font-medium text-gray-200 mb-1">
                    Тип программы
                  </label>
                  <select
                    id="serviceType"
                    className="w-full rounded-md px-3 py-2 text-gray-900"
                    value={serviceType}
                    onChange={(e) => setServiceType(e.target.value as ServiceType)}
                  >
                    <option value="volume">Объёмный подбор (скидка по объёму)</option>
                    <option value="msp">MSP / Вендор-менеджмент</option>
                    <option value="rpo">RPO — выделенная команда</option>
                    <option value="outsourcing">Аутсорсинг функций</option>
                  </select>
                </div>
                <div id="fieldGroup">{renderFields()}</div>
                <button
                  type="submit"
                  className="w-full bg-yellow-400 hover:bg-yellow-500 text-gray-900 py-2 rounded-xl font-semibold"
                >
                  Рассчитать
                </button>
              </form>
            </div>
            <div className="simple-card">
              <h3 className="text-xl font-semibold mb-2">Результат</h3>
              <div id="result" className="text-lg">
                {result}
              </div>
              <p className="text-xs text-gray-300 mt-4">
                * Расчёт ориентировочный. Точные условия — после брифа и согласования SLA.
              </p>
            </div>
          </div>
        </section>

        <section id="faq" className="scroll-mt-32">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Частые вопросы</h2>
          <div className="space-y-3">
            <details className="simple-card">
              <summary className="cursor-pointer font-semibold">Как работает скидка по объёму?</summary>
              <p className="mt-2 text-sm text-gray-200">
                До 10 наймов/мес — 20%; 11–30 — 18%; 31+ — 16%. Комиссия по факту выхода каждого сотрудника.
              </p>
            </details>
            <details className="simple-card">
              <summary className="cursor-pointer font-semibold">Как считается MSP-плата?</summary>
              <p className="mt-2 text-sm text-gray-200">
                Процент от совокупных затрат на подрядчиков/временный персонал. Обычно 6–10% в зависимости от SLA и охвата.
              </p>
            </details>
            <details className="simple-card">
              <summary className="cursor-pointer font-semibold">Что входит в RPO?</summary>
              <p className="mt-2 text-sm text-gray-200">
                Команда рекрутеров/сорсеров на фиксированной ставке, TL, отчётность, интеграции и SLA.
              </p>
            </details>
          </div>
        </section>

        <section id="contact" className="py-12 scroll-mt-32">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Запросить корпоративное коммерческое предложение
          </h2>
          <form onSubmit={handleContactSubmit} className="simple-card p-6 grid gap-4 md:grid-cols-2">
            <input
              name="company"
              value={contactForm.company}
              onChange={handleContactInputChange}
              className="rounded-md px-3 py-2 text-gray-900"
              placeholder="Компания"
              required
            />

            <input
              name="name"
              value={contactForm.name}
              onChange={handleContactInputChange}
              className="rounded-md px-3 py-2 text-gray-900"
              placeholder="Контактное лицо"
              required
            />
            <input
              name="email"
              value={contactForm.email}
              onChange={handleContactInputChange}
              className="rounded-md px-3 py-2 text-gray-900"
              placeholder="E-mail"
              type="email"
              required
            />
            <input
              name="phone"
              value={contactForm.phone}
              onChange={handleContactInputChange}
              className="rounded-md px-3 py-2 text-gray-900"
              placeholder="Телефон"
              type="tel"
              required
            />
            <textarea
              name="message"
              value={contactForm.message}
              onChange={handleContactInputChange}
              className="rounded-md px-3 py-2 text-gray-900 md:col-span-2"
              rows={4}
              placeholder="Кратко опишите задачу и сроки"
              required
            ></textarea>

            <div className="md:col-span-2 flex items-center">
              <input
                type="checkbox"
                id="privacy-agreement"
                name="privacy-agreement"
                checked={isAgreed}
                onChange={handleAgreementChange}
                className="h-4 w-4 text-yellow-400 focus:ring-yellow-500 border-gray-300 rounded"
              />
              <label htmlFor="privacy-agreement" className="ml-2 text-sm text-gray-200">
                Я соглашаюсь с{' '}
                <a href="/privacy" className="underline hover:text-yellow-400">
                  условиями обработки персональных данных
                </a>
              </label>
            </div>

            <button
              type="submit"
              className={`bg-yellow-400 hover:bg-yellow-500 text-gray-900 rounded-xl py-2 font-semibold md:col-span-2 ${isSubmitting ? 'opacity-75 cursor-wait' : ''
                }`}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Отправка...' : 'Отправить'}
            </button>

            {errorMessage && (
              <div className="md:col-span-2 text-center text-sm text-red-400">
                {errorMessage}
              </div>
            )}

            {submitStatus === 'success' && (
              <div className="md:col-span-2 text-center text-sm text-green-300">
                Ваша заявка успешно отправлена!
              </div>
            )}
            {submitStatus === 'error' && (
              <div className="md:col-span-2 text-center text-sm text-red-400">
                Ошибка при отправке. Пожалуйста, попробуйте снова.
              </div>
            )}
          </form>
        </section>
      </main>
    </div>
  );
};

export default Home;